import React from 'react';
import { Box, BoxProps } from '@mui/material';
import { Progress } from './Progress';
import { ProductRecognitionCropScanResponse } from '../../../types';

const addProductRecognitionQueryParam = (imageUrl: string): string => {
  if (!imageUrl) return imageUrl; // Return empty string as-is
  try {
    const url = new URL(imageUrl, window.location.origin);
    url.searchParams.set('pt', '1');
    return url.toString();
  } catch {
    // If URL is invalid, return original
    return imageUrl;
  }
};

const getClassificationColor = (classificationType: string): string => {
  switch (classificationType) {
    case 'product':
      return '#4CAF50'; // Green for products
    case 'carton':
      return '#FF9800'; // Orange for cartons
    default:
      return '#2196F3'; // Blue for unknown/other types
  }
};

interface ExtendedProductRecognitionCropScanResponse
  extends ProductRecognitionCropScanResponse {
  color?: string; // Optional color for classification
  opacity?: number; // Optional opacity for rendering
}

interface ProductRecognitionViewerProps extends Omit<BoxProps, 'component'> {
  imageUrl: string;
  cropScans: ExtendedProductRecognitionCropScanResponse[];
  width?: number;
  height?: number;
  mode?: 'full' | 'crop'; // 'full' shows entire image with all crops, 'crop' shows focused crop view
  focusCropScan?: ExtendedProductRecognitionCropScanResponse; // Used in 'crop' mode to focus on specific crop
  onCropScanClick?: (
    cropScan: ProductRecognitionCropScanResponse,
    index?: number
  ) => void;
  onClick?: () => void; // General click handler for the entire component
  hidePolygons?: boolean; // Optional prop to hide polygons
}

export const ProductRecognitionViewer: React.FC<
  ProductRecognitionViewerProps
> = ({
  imageUrl,
  cropScans,
  width = 600,
  height = 400,
  mode = 'full',
  focusCropScan,
  onCropScanClick,
  onClick,
  hidePolygons,
  ...boxProps
}) => {
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageDimensions, setImageDimensions] = React.useState({
    width: 0,
    height: 0,
  });

  // Add pt=1 query parameter for product recognition
  const processedImageUrl = React.useMemo(
    () => addProductRecognitionQueryParam(imageUrl),
    [imageUrl]
  );

  React.useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      setImageLoaded(true);
    };
    img.src = processedImageUrl;
  }, [processedImageUrl]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      e.stopPropagation();
      onClick();
    }
  };

  const handleCropScanClick = (
    cropScan: ProductRecognitionCropScanResponse,
    index: number
  ) => {
    if (onCropScanClick) {
      onCropScanClick(cropScan, index);
    } else if (onClick) {
      onClick();
    }
  };

  if (!imageLoaded) {
    return (
      <Box
        {...boxProps}
        sx={{
          width: '100%',
          maxWidth: width,
          height: 'auto',
          minHeight: Math.min(200, height),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          borderRadius: 1,
          position: 'relative',
          ...boxProps.sx,
        }}
      >
        <Progress size={24} absolute />
      </Box>
    );
  }

  // Calculate dimensions and viewBox based on mode
  let svgWidth = width;
  let svgHeight = height;
  let viewBox = `0 0 ${imageDimensions.width} ${imageDimensions.height}`;
  let cursor = 'default';

  if (mode === 'crop' && focusCropScan) {
    // Crop mode: focus on specific crop scan with padding
    const cropPadding = 20; // Increased padding for better visual containment
    const cropX = Math.max(0, focusCropScan.bbox[0] - cropPadding);
    const cropY = Math.max(0, focusCropScan.bbox[1] - cropPadding);
    const cropWidth = Math.min(
      imageDimensions.width - cropX,
      focusCropScan.bbox[2] + cropPadding * 2
    );
    const cropHeight = Math.min(
      imageDimensions.height - cropY,
      focusCropScan.bbox[3] + cropPadding * 2
    );

    viewBox = `${cropX} ${cropY} ${cropWidth} ${cropHeight}`;
    cursor = onClick ? 'pointer' : 'default';

    // Keep SVG dimensions matching the component's width/height props
    // The viewBox will handle the cropping and the SVG will scale to fit
    svgWidth = width;
    svgHeight = height;
  } else if (mode === 'full') {
    // Full mode: scale image to fit container while maintaining aspect ratio
    const imageAspectRatio = imageDimensions.width / imageDimensions.height;
    const containerAspectRatio = width / height;

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider - fit to width
      svgHeight = width / imageAspectRatio;
    } else {
      // Image is taller - fit to height
      svgWidth = height * imageAspectRatio;
    }
  }

  // Determine which crop scans to render
  const cropsToRender =
    mode === 'crop' && focusCropScan ? [focusCropScan] : cropScans;

  return (
    <Box
      {...boxProps}
      sx={{
        width: '100%',
        maxWidth: width,
        height: 'auto',
        minHeight: mode === 'crop' ? 'auto' : 200,
        position: 'relative',
        cursor,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...boxProps.sx,
      }}
      onClick={handleClick}
    >
      <svg
        width={svgWidth}
        height={svgHeight}
        viewBox={viewBox}
        style={{
          display: 'block',
          maxWidth: '100%',
          height: 'auto',
        }}
      >
        <image
          href={processedImageUrl}
          width={imageDimensions.width}
          height={imageDimensions.height}
        />

        {/* Render crop scans */}
        {cropsToRender.map((cropScan, index) => {
          const color =
            cropScan.color ??
            getClassificationColor(cropScan.classification_type);
          const opacity = cropScan.opacity ?? (cropScan.barcode ? 0.7 : 0.3);
          const cropIndex =
            mode === 'crop'
              ? cropScans.findIndex(
                  (cs) =>
                    cs.product_crop_scan_id === cropScan.product_crop_scan_id
                )
              : index;

          return (
            <g key={cropScan.product_crop_scan_id || index}>
              {/* Render polygon if points exist */}
              {!hidePolygons &&
                cropScan.points &&
                cropScan.points.length > 0 && (
                  <polygon
                    points={cropScan.points
                      .map((point: any) => `${point.x},${point.y}`)
                      .join(' ')}
                    fill={color}
                    fillOpacity={opacity}
                    stroke={color}
                    strokeWidth={3}
                    style={{ cursor: onCropScanClick ? 'pointer' : 'default' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCropScanClick(cropScan, cropIndex);
                    }}
                    vectorEffect="non-scaling-stroke"
                  />
                )}

              {/* Render rectangle for box */}
              {cropScan.bbox && cropScan.bbox.length >= 4 && (
                <rect
                  x={cropScan.bbox[0]}
                  y={cropScan.bbox[1]}
                  width={cropScan.bbox[2]}
                  height={cropScan.bbox[3]}
                  fill={color}
                  fillOpacity={opacity}
                  stroke={color}
                  strokeWidth={3}
                  style={{ cursor: onCropScanClick ? 'pointer' : 'default' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCropScanClick(cropScan, cropIndex);
                  }}
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </g>
          );
        })}
      </svg>
    </Box>
  );
};
